import React, { useState } from "react";
import { ReactComponent as CloseIcon } from "../../assets/icons/svg/cancel-icon.svg";

interface PreviewPopupProps {
  creative: {
    imageUrl: string;
    title: string;
  };
  onClose: () => void;
}

const PreviewPopup: React.FC<PreviewPopupProps> = ({ creative, onClose }) => {
  const [imageError, setImageError] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const handleImageError = () => setImageError(true);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const { naturalWidth, naturalHeight } = img;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const padding = 64; // Total padding: 32px on each side
    const headerHeight = 80;
    const marginVertical = 96; // 48px top + 48px bottom
    const maxWidth = viewportWidth - padding;
    const maxHeight = viewportHeight - headerHeight - padding - marginVertical;

    let width = naturalWidth;
    let height = naturalHeight;
    const aspectRatio = width / height;

    if (width > maxWidth || height > maxHeight) {
      if (width / aspectRatio <= maxHeight) {
        width = maxWidth;
        height = width / aspectRatio;
      } else {
        height = maxHeight;
        width = height * aspectRatio;
      }
    }

    setImageSize({ width, height });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-auto">
      <div
        className="bg-white rounded-xl shadow-xl p-6 relative my-12"
        style={{
          width: `${imageSize.width + 64}px`,
          height: `${imageSize.height + 80 + 64}px`,
          maxWidth: "95vw",
          maxHeight: "95vh",
          marginTop: "48px", // extra top space
          marginBottom: "48px", // extra bottom space
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Preview</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close preview"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        <hr className="border-t border-gray-200 mb-4" />

        {/* Image */}
        <div className="flex justify-center items-center bg-black rounded-lg p-2">
          {!imageError ? (
            <img
              src={creative.imageUrl}
              alt={creative.title}
              onLoad={handleImageLoad}
              onError={handleImageError}
              className="rounded object-contain"
              style={{
                width: `${imageSize.width}px`,
                height: `${imageSize.height}px`,
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 p-8 bg-gray-100 rounded min-h-[200px] w-[300px]">
              <svg
                className="w-16 h-16 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span className="text-lg">Preview not available</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewPopup;
